import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { getPublicTrombinos } from "../../../services/trombinosServices";

import {
  ensureArray,
  normalizeSearchText,
  normalizeTrombinoPerson,
  sortTrombinos,
} from "../utils/trombinosUtils";

const INITIAL_FILTERS = {
  q: "",
  tab: "all",
  serviceId: "all",
};

export function useTrombinosPage() {
  const bootedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [allItems, setAllItems] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [photoPreview, setPhotoPreview] = useState(null);

  const deferredSearch = useDeferredValue(filters.q);

  const loadTrombinos = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getPublicTrombinos({
        sort: "ordre",
        dir: "ASC",
      });

      const rows = sortTrombinos(
        ensureArray(response).map(normalizeTrombinoPerson),
      );

      setAllItems(rows);
    } catch (err) {
      console.error("Erreur chargement trombinoscope public:", err);
      setAllItems([]);
      setError("LOAD_ERROR");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    loadTrombinos();
  }, [loadTrombinos]);

  const services = useMemo(() => {
    const map = new Map();

    allItems.forEach((person) => {
      if (person.serviceId && person.serviceLabel) {
        map.set(String(person.serviceId), person.serviceLabel);
      }
    });

    return Array.from(map.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label, "fr"));
  }, [allItems]);

  const stats = useMemo(() => {
    const elected = allItems.filter((item) => item.isElected).length;
    const personnel = allItems.filter((item) => !item.isElected).length;

    return {
      total: allItems.length,
      elected,
      personnel,
    };
  }, [allItems]);

  const filteredItems = useMemo(() => {
    const q = normalizeSearchText(deferredSearch);

    const filtered = allItems.filter((person) => {
      const matchTab =
        filters.tab === "all"
          ? true
          : filters.tab === "elected"
            ? person.isElected
            : !person.isElected;

      const matchService =
        filters.serviceId === "all"
          ? true
          : String(person.serviceId) === String(filters.serviceId);

      const matchSearch = q ? person.searchIndex.includes(q) : true;

      return matchTab && matchService && matchSearch;
    });

    return sortTrombinos(filtered);
  }, [allItems, deferredSearch, filters.serviceId, filters.tab]);

  const updateFilter = useCallback((name, value) => {
    setPhotoPreview(null);

    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setPhotoPreview(null);
  }, []);

  const openPhoto = useCallback((person) => {
    setPhotoPreview({
      src: person.photo,
      alt: person.fullName,
      subtitle: person.fonction || person.roleLabel,
    });
  }, []);

  const closePhoto = useCallback(() => {
    setPhotoPreview(null);
  }, []);

  const refresh = useCallback(() => {
    setPhotoPreview(null);
    loadTrombinos();
  }, [loadTrombinos]);

  return {
    loading,
    error,
    items: filteredItems,
    allTotal: allItems.length,
    total: filteredItems.length,
    services,
    stats,
    filters,
    photoPreview,
    updateFilter,
    resetFilters,
    openPhoto,
    closePhoto,
    refresh,
  };
}
